import { useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { TextArea } from "@/components/design/TextField";
import { MenuItem } from "@/components/design/MenuItem";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { ShareIcon } from "@/components/share/ShareItemCard";
import { formatDateTime } from "@/lib/format-date";
import { logApplicationFailure, userErrorMessage } from "@/lib/user-error";

export interface DiscussionComment {
  id: string;
  content?: string | null;
  postedAt?: string | null;
  person?: { id: string; formattedName?: string | null; avatar?: { content?: string | null; mimeType?: string | null } | null } | null;
}
export interface DiscussionThread {
  comments: readonly DiscussionComment[];
  totalCount?: number;
  loading: boolean;
  error: Error | null;
  busy: boolean;
  hasNextPage?: boolean;
  loadMore?: () => Promise<unknown>;
  add?: (content: string) => Promise<void>;
  edit?: (id: string, content: string) => Promise<void>;
  remove: (id: string) => Promise<void>;
}
export interface DiscussionDrafts {
  text: string;
  onTextChange: (text: string) => void;
  edits: Readonly<Record<string, { content: string; dirty: boolean }>>;
  onEdit: (comment: DiscussionComment) => void;
  onEditChange: (comment: DiscussionComment, text: string) => void;
  onDiscard: (comment: DiscussionComment) => void;
}
type Composer = (comment: DiscussionComment | null, disabled: boolean) => ReactNode;

// SharedItem sends comments directly. OrganizationalRecords supply page-owned drafts for the shared SaveBar.
export function CommentArea({ thread, meId, canWrite = true, embedded = false, blockedMessage, drafts, renderComposer, renderCommentExtras, renderAuthor }: {
  thread: DiscussionThread;
  meId: string | null;
  canWrite?: boolean;
  /** The enclosing section supplies the surface and heading. */
  embedded?: boolean;
  blockedMessage?: ReactNode;
  drafts?: DiscussionDrafts;
  renderComposer?: Composer;
  renderCommentExtras?: (comment: DiscussionComment) => ReactNode;
  renderAuthor?: (comment: DiscussionComment) => ReactNode;
}) {
  const intl = useIntl();
  const [localText, setLocalText] = useState("");
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const text = drafts?.text ?? localText;
  const busy = thread.busy || deleteBusy;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (drafts || !text.trim() || Array.from(text.trim()).length > 4096 || busy || !thread.add) return;
    try { await thread.add(text); setLocalText(""); }
    catch { logApplicationFailure("share.create-comment"); }
  }
  async function confirmDelete() {
    if (!deleting || busy) return;
    setDeleteBusy(true);
    try { await thread.remove(deleting); setDeleting(null); }
    catch (failure) { setDeleteError(userErrorMessage(intl, failure)); }
    finally { setDeleteBusy(false); }
  }
  return <div className={embedded ? "flex w-full max-w-[48rem] flex-col" : "box flex flex-col overflow-hidden bg-surface"}>
    {!embedded && <div className="flex items-center justify-between gap-2 border-b border-ink/5 px-4 py-3">
      <span className="smycles-field-label"><FormattedMessage id="share.itemDetail.comments" defaultMessage="Comments" /></span>
      <span className="smycles-field-meta tabular-nums text-ink-muted">{thread.totalCount}</span>
    </div>}
    {!canWrite ? blockedMessage && <div className="border-b border-ink/5 p-3 smycles-field-meta text-ink-muted">{blockedMessage}</div> :
      <form onSubmit={submit} className={`space-y-2 border-b border-ink/10 ${embedded ? "pb-4" : "p-3"}`}>
        <TextArea id="comment-new" value={text} onChange={event => drafts ? drafts.onTextChange(event.target.value) : setLocalText(event.target.value)}
          disabled={busy} rows={2} aria-label={intl.formatMessage({ id: "share.itemDetail.commentPlaceholder" })}
          placeholder={intl.formatMessage({ id: "share.itemDetail.commentPlaceholder", defaultMessage: "Write a comment…" })} />
        {renderComposer?.(null, busy)}
        {!drafts && <div className="flex justify-end"><Button id="comment-send" type="submit" size="sm" disabled={busy || !text.trim() || Array.from(text.trim()).length > 4096}>
          <FormattedMessage id="share.itemDetail.send" defaultMessage="Send" />
        </Button></div>}
      </form>}
    <div className={embedded ? undefined : "max-h-[60vh] min-h-[18rem] overflow-y-auto px-4"}>
      {thread.loading ? <p className="py-4 smycles-field-meta text-ink-muted"><FormattedMessage id="share.itemDetail.loading" defaultMessage="loading…" /></p>
        : thread.error ? <p role="alert" className="py-4 smycles-field-meta">{userErrorMessage(intl, thread.error)}</p>
        : !thread.comments.length ? <p className="py-4 smycles-field-meta text-ink-muted"><FormattedMessage id="share.itemDetail.noComments" defaultMessage="No comments yet, be the first." /></p>
        : thread.comments.map(comment => <CommentRow key={comment.id} comment={comment}
          mine={canWrite && Boolean(meId && comment.person?.id === meId)} busy={busy} drafts={drafts}
          onEdit={content => thread.edit?.(comment.id, content) ?? Promise.resolve()}
          onDelete={() => { setDeleteError(null); setDeleting(comment.id); }} renderComposer={renderComposer} extras={renderCommentExtras?.(comment)} author={renderAuthor?.(comment)} />)}
      {thread.hasNextPage && <Button variant="secondary" disabled={thread.loading || busy}
        onClick={() => void thread.loadMore?.().catch(() => logApplicationFailure("comments.load-more"))}>
        <FormattedMessage id="common.loadMore" defaultMessage="Load more" />
      </Button>}
    </div>
    <ConfirmDialog open={Boolean(deleting)} title={<FormattedMessage id="comments.delete.title" />}
      message={<><FormattedMessage id="comments.delete.body" />{deleteError && <p role="alert" className="mt-3">{deleteError}</p>}</>}
      confirmLabel={<FormattedMessage id={deleteBusy ? "common.saving" : "common.delete"} />}
      onCancel={() => { if (!deleteBusy) setDeleting(null); }} onConfirm={confirmDelete} />
  </div>;
}

function CommentRow({ comment, mine, busy, onEdit, onDelete, drafts, renderComposer, extras, author }: {
  comment: DiscussionComment; mine: boolean; busy: boolean;
  onEdit: (content: string) => Promise<void>; onDelete: () => void;
  drafts?: DiscussionDrafts; renderComposer?: Composer; extras?: ReactNode; author?: ReactNode;
}) {
  const intl = useIntl();
  const [menuOpen, setMenuOpen] = useState(false);
  const [localEditing, setLocalEditing] = useState(false);
  const [localDraft, setLocalDraft] = useState(comment.content ?? "");
  const [collapsed, setCollapsed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const pending = drafts?.edits[comment.id];
  const editing = drafts ? Boolean(pending) && !collapsed : localEditing;
  const text = pending?.content ?? localDraft;
  const dirty = pending?.dirty ?? (text !== (comment.content ?? ""));
  const cancel = () => { drafts?.onDiscard(comment); setLocalEditing(false); setCollapsed(false); setDiscarding(false); };
  async function submitEdit() {
    if (!text.trim() || Array.from(text.trim()).length > 4096 || saving || busy) return;
    setSaving(true);
    try { if (dirty) await onEdit(text.trim()); setLocalEditing(false); }
    catch { logApplicationFailure("share.edit-comment"); }
    finally { setSaving(false); }
  }
  return <article id={`comment-${comment.id}`} className="border-b border-ink/10 py-4 last:border-b-0 last:pb-0">
    <div className="flex items-start justify-between gap-2">
      <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1">
        {author ?? <span className="smycles-field-meta font-medium">{comment.person?.formattedName || "–"}</span>}
        <time dateTime={comment.postedAt ?? undefined} className="smycles-field-meta whitespace-nowrap text-ink-muted">
          {formatDateTime(comment.postedAt, intl.locale) || "–"}
        </time>
      </div>
      <div className="flex shrink-0 items-center">
        {mine && <div className="relative">
          <Button id={`comment-${comment.id}-more`} variant="icon" size="sm" disabled={busy} onClick={() => setMenuOpen(value => !value)}
            aria-label={intl.formatMessage({ id: "share.itemDetail.more", defaultMessage: "More" })} aria-haspopup="menu" aria-expanded={menuOpen}>
            <ShareIcon name="dots" className="h-4 w-4" />
          </Button>
          {menuOpen && <><div aria-hidden onClick={() => setMenuOpen(false)} className="fixed inset-0 z-[var(--z-dropdown)]" />
            <div role="menu" className="box bg-surface p-1 absolute right-0 top-full z-[var(--z-dropdown)] mt-1 w-36">
              <MenuItem id={`comment-${comment.id}-edit`} role="menuitem" icon={<ShareIcon name="pencil" className="h-3.5 w-3.5" />} onClick={() => {
                setMenuOpen(false); setCollapsed(false);
                if (drafts) drafts.onEdit(comment); else { setLocalDraft(comment.content ?? ""); setLocalEditing(true); }
              }}><FormattedMessage id="common.edit" defaultMessage="Edit" /></MenuItem>
              <MenuItem id={`comment-${comment.id}-delete`} role="menuitem" icon={<ShareIcon name="trash" className="h-3.5 w-3.5" />} disabled={busy}
                onClick={() => { setMenuOpen(false); onDelete(); }}><FormattedMessage id="common.delete" defaultMessage="Delete" /></MenuItem>
            </div></>}
        </div>}
      </div>
    </div>
    <div className={author ? "pl-9" : undefined}>
    {editing ? <div className="mt-2 space-y-2">
      <TextArea id={`comment-${comment.id}-input`} value={text} onChange={event => drafts ? drafts.onEditChange(comment, event.target.value) : setLocalDraft(event.target.value)} rows={3} autoFocus disabled={saving || busy} />
      {renderComposer?.(comment, saving || busy)}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" size="sm" disabled={saving || busy} onClick={() => dirty ? setDiscarding(true) : cancel()}><FormattedMessage id="common.cancel" defaultMessage="Cancel" /></Button>
        <Button id={`comment-${comment.id}-save`} size="sm" disabled={saving || busy || !text.trim() || Array.from(text.trim()).length > 4096}
          onClick={() => {
            if (drafts) { if (!dirty) drafts.onDiscard(comment); setCollapsed(true); }
            else void submitEdit();
          }}><FormattedMessage id={drafts ? "search.done" : "common.save"} /></Button>
      </div>
    </div> : <><p id={`comment-${comment.id}-text`} className="mt-2 max-w-[65ch] smycles-field-value whitespace-pre-wrap break-words leading-relaxed">{pending?.content ?? comment.content}</p>
      {extras && <div className="mt-3">{extras}</div>}
      {pending?.dirty && <p className="smycles-field-meta text-ink-muted"><FormattedMessage id="unsavedGuard.title" /></p>}</>}
    </div>
    <ConfirmDialog open={discarding} title={<FormattedMessage id="unsavedGuard.title" />} message={<FormattedMessage id="comments.discard.body" />}
      confirmLabel={<FormattedMessage id="common.discard" />} onCancel={() => setDiscarding(false)} onConfirm={cancel} />
  </article>;
}
